/**
 * Mapbox Geocoding v6 client, biased to Thailand and to the driver's position.
 *
 * `proximity` matters a lot here: searching "7-eleven" without it returns
 * results from across the country, with it you get the one you're driving past.
 */

import { MAPBOX_TOKEN, REGION } from '../core/config';
import type { LngLat, Place } from '../core/types';
import { distance } from '../core/geo';
import { getLang, t } from '../core/i18n';

interface V6Feature {
  id: string;
  properties: {
    name?: string;
    name_preferred?: string;
    full_address?: string;
    place_formatted?: string;
    feature_type?: string;
    coordinates?: { longitude: number; latitude: number };
  };
  geometry?: { type: 'Point'; coordinates: [number, number] };
}

function toPlace(f: V6Feature, origin: LngLat | null): Place | null {
  const c = f.properties.coordinates;
  const coords: LngLat | null = c
    ? [c.longitude, c.latitude]
    : f.geometry?.coordinates
      ? [f.geometry.coordinates[0], f.geometry.coordinates[1]]
      : null;
  if (!coords) return null;

  const name = f.properties.name_preferred || f.properties.name || t('place.droppedPin');
  const address = f.properties.full_address || f.properties.place_formatted || '';

  return {
    id: f.id,
    name,
    address,
    center: coords,
    kind: f.properties.feature_type ?? 'place',
    distance: origin ? distance(origin, coords) : undefined,
  };
}

/** Parse "13.7563, 100.5018" typed straight into the search box. */
function parseCoordinateQuery(q: string, origin: LngLat | null): Place | null {
  const m = q.trim().match(/^(-?\d{1,2}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1]);
  const lng = Number(m[2]);
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  const center: LngLat = [lng, lat];
  return {
    id: `coord:${lat},${lng}`,
    name: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
    address: t('place.coordinates'),
    center,
    kind: 'coordinate',
    distance: origin ? distance(origin, center) : undefined,
  };
}

/** Search Box /forward feature shape (only the fields we read). */
interface SearchBoxFeature {
  geometry?: { type: 'Point'; coordinates: [number, number] };
  properties: {
    mapbox_id?: string;
    name?: string;
    name_preferred?: string;
    full_address?: string;
    place_formatted?: string;
    feature_type?: string;
    poi_category?: string[];
  };
}

/**
 * Primary search: Mapbox Search Box - real POI/business-name search ("Google
 * Maps style": brands, shops, restaurants), proximity-biased. Falls back to
 * the Geocoding API when unavailable so address search always works.
 */
export async function searchPlaces(
  query: string,
  origin: LngLat | null,
  signal?: AbortSignal,
): Promise<Place[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const coordHit = parseCoordinateQuery(q, origin);
  if (coordHit) return [coordHit];

  const p = origin ?? REGION.fallbackCenter;

  try {
    const params = new URLSearchParams({
      q,
      access_token: MAPBOX_TOKEN,
      // Over-fetch so nearest-first sorting has a real pool to work with.
      limit: '10',
      language: getLang(),
      country: REGION.country,
      proximity: `${p[0]},${p[1]}`,
      types: 'poi,brand,address,place,street,neighborhood',
    });
    const res = await fetch(`https://api.mapbox.com/search/searchbox/v1/forward?${params}`, {
      signal,
    });
    if (res.ok) {
      const body = (await res.json()) as { features?: SearchBoxFeature[] };
      let places = (body.features ?? [])
        .map((f): Place | null => {
          const coords = f.geometry?.coordinates;
          if (!coords) return null;
          const category = f.properties.poi_category?.[0];
          return {
            id: f.properties.mapbox_id ?? `sb:${coords.join(',')}`,
            name: f.properties.name_preferred || f.properties.name || t('place.droppedPin'),
            address:
              [category, f.properties.full_address ?? f.properties.place_formatted]
                .filter(Boolean)
                .join(' · ') || '',
            center: [coords[0], coords[1]],
            kind: f.properties.feature_type ?? 'poi',
            distance: origin ? distance(origin, [coords[0], coords[1]]) : undefined,
          };
        })
        .filter((x): x is Place => x !== null);

      // Brand/POI queries ("7 eleven", "ptt"): the driver wants the NEAREST
      // branches, not the most textually-relevant ones. When the results are
      // dominated by POIs and we know where we are, sort by distance outright.
      if (origin && places.length) {
        const poiShare = places.filter((x) => x.kind === 'poi' || x.kind === 'brand').length / places.length;
        if (poiShare >= 0.6) {
          places = [...places].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
        }
      }
      if (places.length) return places.slice(0, 8);
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    /* fall through to geocoding */
  }

  const params = new URLSearchParams({
    q,
    access_token: MAPBOX_TOKEN,
    limit: '8',
    language: getLang(),
    country: REGION.country,
  });
  params.set('proximity', `${p[0]},${p[1]}`);

  const res = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?${params}`, { signal });
  if (!res.ok) throw new Error(`Search failed (${res.status})`);

  const body = (await res.json()) as { features?: V6Feature[] };
  return (body.features ?? [])
    .map((f) => toPlace(f, origin))
    .filter((p): p is Place => p !== null);
}

/** Name a coordinate, used when the driver long-presses the map to drop a pin. */
export async function reverseGeocode(point: LngLat, signal?: AbortSignal): Promise<Place> {
  const fallback: Place = {
    id: `pin:${point[0]},${point[1]}`,
    name: t('place.droppedPin'),
    address: `${point[1].toFixed(5)}, ${point[0].toFixed(5)}`,
    center: point,
    kind: 'pin',
  };

  try {
    const params = new URLSearchParams({
      longitude: String(point[0]),
      latitude: String(point[1]),
      access_token: MAPBOX_TOKEN,
      limit: '1',
      language: getLang(),
    });
    const res = await fetch(`https://api.mapbox.com/search/geocode/v6/reverse?${params}`, { signal });
    if (!res.ok) return fallback;
    const body = (await res.json()) as { features?: V6Feature[] };
    const first = body.features?.[0];
    if (!first) return fallback;
    return toPlace(first, null) ?? fallback;
  } catch {
    return fallback;
  }
}
